import { Link } from "react-router-dom";

import type { AdminLabCertification, AdminLabRow } from "../../services/labService";
import { Badge } from "../ui/Badge";
import { AdminLabActionsMenu } from "./AdminLabActionsMenu";

interface AdminLabsListRowProps {
  lab: AdminLabRow;
  showTypeBadge: boolean;
  isBusy: boolean;
  onOpenMembers: (lab: AdminLabRow) => void;
  onManageAuditor: (lab: AdminLabRow) => void;
  onJoin: (labId: string) => Promise<void>;
  onLeave: (labId: string) => Promise<void>;
  onRemove: (lab: AdminLabRow) => void;
}

const formatFte = (value: number) => {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

const levelBadgeClassName = (certification: AdminLabCertification | null) => {
  if (!certification) {
    return "badge badge-outline badge-sm";
  }

  if (certification.level === "gold") {
    return "badge badge-sm border-yellow-300 bg-yellow-100 text-yellow-900";
  }
  if (certification.level === "silver") {
    return "badge badge-sm border-slate-300 bg-slate-100 text-slate-700";
  }
  return "badge badge-sm border-amber-300 bg-amber-100 text-amber-900";
};

const workflowBadgeClassName = (workflowStatus: AdminLabCertification["workflow_status"]) => {
  switch (workflowStatus) {
    case "in_progress":
      return "badge badge-sm border-blue-300 bg-blue-100 text-blue-800";
    case "ready_to_assign":
      return "badge badge-sm border-orange-300 bg-orange-100 text-orange-800";
    case "assigned":
      return "badge badge-sm border-sky-300 bg-sky-100 text-sky-800";
    case "certified":
      return "badge badge-sm border-emerald-300 bg-emerald-100 text-emerald-800";
    case "expired":
      return "badge badge-sm border-stone-300 bg-stone-100 text-stone-700";
    default:
      return "badge badge-sm badge-outline";
  }
};

const formatWorkflowLabel = (workflowStatus: AdminLabCertification["workflow_status"]) => {
  return workflowStatus
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatLevelLabel = (certification: AdminLabCertification | null) => {
  if (!certification) {
    return "No certification";
  }

  return certification.level.charAt(0).toUpperCase() + certification.level.slice(1);
};

const formatAuditorLabel = (certification: AdminLabCertification | null) => {
  if (!certification) {
    return "No active certification";
  }

  if (certification.auditor?.name || certification.auditor?.email) {
    return certification.auditor.name || certification.auditor.email || "Assigned";
  }

  if (certification.pending_auditor_invite?.state === "accepted") {
    return "Invite accepted";
  }

  if (certification.pending_auditor_invite?.state === "expired") {
    return "Invite expired";
  }

  if (certification.pending_auditor_invite?.state === "pending") {
    return "Invite pending";
  }

  return "Unassigned";
};

export const AdminLabsListRow = ({
  lab,
  showTypeBadge,
  isBusy,
  onOpenMembers,
  onManageAuditor,
  onJoin,
  onLeave,
  onRemove,
}: AdminLabsListRowProps) => {
  const championLabel = lab.champion?.name || lab.champion?.email || "No champion";
  const certification = lab.current_certification;
  const auditorLabel = formatAuditorLabel(certification);

  return (
    <div className="flex flex-col gap-4 py-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="grid flex-1 gap-4 md:grid-cols-2 xl:grid-cols-[minmax(0,1.7fr)_minmax(0,1.3fr)_minmax(0,1.4fr)]">
        <div className="min-w-0 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-base-content/60">
            Lab
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {lab.can_open_lab ? (
              <Link
                to={`/lab/${lab.id}`}
                className="truncate text-lg font-semibold text-primary hover:underline"
              >
                {lab.name}
              </Link>
            ) : (
              <p className="truncate text-lg font-semibold">{lab.name}</p>
            )}
            {showTypeBadge && <Badge variant="primary">{lab.type === "wet" ? "Wet" : "Dry"}</Badge>}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-base-content/60">
            Operations
          </p>
          <div className="space-y-1 text-sm">
            <div>
              <span className="text-base-content/70">Members: </span>
              {lab.can_view_members ? (
                <button
                  type="button"
                  className="font-semibold text-primary hover:underline"
                  onClick={() => onOpenMembers(lab)}
                >
                  {lab.member_count} / {formatFte(lab.total_fte)}
                </button>
              ) : (
                <span className="font-semibold">
                  {lab.member_count} / {formatFte(lab.total_fte)}
                </span>
              )}
            </div>
            <div title={lab.champion?.email || undefined}>
              <span className="text-base-content/70">Champion: </span>
              <span className="font-medium">{championLabel}</span>
            </div>
            <div title={certification?.auditor?.email || certification?.pending_auditor_invite?.email || undefined}>
              <span className="text-base-content/70">Auditor: </span>
              <span className="font-medium">{auditorLabel}</span>
              {certification?.pending_auditor_invite?.email && (
                <p className="mt-1 text-xs text-base-content/60">
                  {certification.pending_auditor_invite.email}
                </p>
              )}
              {certification?.auditor_status_detail && (
                <p className="mt-1 text-xs text-base-content/60">
                  {certification.auditor_status_detail}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-base-content/60">
            Certification
          </p>
          <div className="flex flex-wrap gap-2">
            <span className={levelBadgeClassName(certification)}>
              {formatLevelLabel(certification)}
            </span>
            {certification && (
              <span className={workflowBadgeClassName(certification.workflow_status)}>
                {formatWorkflowLabel(certification.workflow_status)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <AdminLabActionsMenu
          lab={lab}
          isBusy={isBusy}
          onManageAuditor={onManageAuditor}
          onJoin={onJoin}
          onLeave={onLeave}
          onRemove={onRemove}
        />
      </div>
    </div>
  );
};